import { membersAPI, iamGroupsAPI, usersAPI, peoplePlatformAPI } from '@/services';
import {
  toUsersArray,
  mapPeopleToMemberRow,
  buildPeopleMemberPayload,
  normalizeCommissionsPayload,
  normalizeContributionsPayload,
  buildUsersRoleIndex,
  buildUsersPositionIndex,
  resolveIamRoleForPerson,
  resolveProjectPositionIdForPerson,
  findLinkedIamUserFromUsers,
} from '../models/memberModel';

export const memberController = {
  async fetchCommissions(params = {}) {
    const response = await iamGroupsAPI.getAll(params);
    const normalized = normalizeCommissionsPayload(response.data).map((g) => ({
      id: g?.id || g?._id || '',
      name: g?.name || g?.title || g?.label || 'Sans nom',
      description: g?.description || '',
      created_at: g?.created_at || null,
      members_count: g?.members_count || 0,
      source: 'iam',
    }));
    const total = Array.isArray(response?.data)
      ? normalized.length
      : (response?.data?.total || normalized.length);
    return { rows: normalized, total };
  },

  async createCommission(data) {
    return iamGroupsAPI.create(data);
  },

  async updateCommission(id, data) {
    return iamGroupsAPI.update(id, data);
  },

  async deleteCommission(id) {
    return iamGroupsAPI.delete(id);
  },

  async fetchUsers(limit = 500, skip = 0) {
    const usersRes = await usersAPI.getAll({ limit, skip });
    return toUsersArray(usersRes?.data);
  },

  async fetchMembersView({ params, currentProjectId }) {
    const peopleRes = await peoplePlatformAPI.list(params);
    const peopleRaw = peopleRes?.data?.people || [];
    const users = await this.fetchUsers(500, 0);
    const usersIndex = buildUsersRoleIndex(users);
    const positionIndex = buildUsersPositionIndex(users, currentProjectId);

    const peopleList = currentProjectId
      ? peopleRaw.filter((p) => {
          const pid = p?.metadata?.project_id || p?.project_id || null;
          return !pid || String(pid) === String(currentProjectId);
        })
      : peopleRaw;

    const list = peopleList.map((p) => ({
      ...mapPeopleToMemberRow(p),
      role: resolveIamRoleForPerson(p, usersIndex),
      position_id: resolveProjectPositionIdForPerson(p, positionIndex) || '',
      member_id: p.id,
    }));
    const total = peopleRes?.data?.total ?? list.length;
    return { list, total };
  },

  async fetchMemberForEdit(id) {
    const res = await peoplePlatformAPI.getById(id);
    return { member: mapPeopleToMemberRow(res.data) };
  },

  async saveMember(id, formData, { currentProjectId, selfEdit = false }) {
    await peoplePlatformAPI.update(
      id,
      buildPeopleMemberPayload(formData, currentProjectId, { selfEdit })
    );
    if (selfEdit) {
      return;
    }
    if (formData.status === 'inactive') {
      await peoplePlatformAPI.deactivate(id);
    } else {
      await peoplePlatformAPI.reactivate(id);
    }
  },

  async fetchMemberDetails(id) {
    const pr = await peoplePlatformAPI.getById(id);
    const memberRow = mapPeopleToMemberRow(pr.data);

    let linked = null;
    try {
      const r = await usersAPI.getByMemberId(id);
      linked = r?.data ?? null;
    } catch (e) {
      if (e?.response?.status !== 404) throw e;
    }
    if (!linked) {
      const users = await this.fetchUsers(500, 0);
      linked = findLinkedIamUserFromUsers(id, memberRow, users);
    }

    const [contribRes, statsRes] = await Promise.all([
      membersAPI.getContributions(id),
      membersAPI.getStats(id),
    ]);

    return {
      member: {
        ...memberRow,
        iamRole: linked?.role ? String(linked.role) : '',
        iamGroupIds: Array.isArray(linked?.group_ids) ? linked.group_ids : [],
      },
      linkedUser: linked || null,
      contributions: normalizeContributionsPayload(contribRes.data),
      stats: statsRes.data,
    };
  },
};
